import { useCallback, useRef, useState } from 'react';
import { ExternalLink, File, FileText } from 'lucide-react';

import { useTracker } from '@archipro-website/tracker';
import { formatFileSize } from '@modules/build-hub/utils/format-document-attributes';
import { DownloadFileRow } from '~/ui-rocco/pages/detail-shared';
import type { RevampDownload } from '../type';

/**
 * Articles have no file categories, so the column that names the file's kind
 * on the product page reads the same for every article file.
 */
const FILE_LABEL = 'Files';

/** Uploader shown when the article credits no professional. */
const ARCHIPRO = 'ArchiPro';

interface RevampDownloadRowProps {
    downloads: RevampDownload[];
    /** Article the files hang off, sent with the download event. */
    articleID: number;
    /** Credited professional, used as the event label and the uploader. */
    professionalTitle?: string;
}

interface ArticleFileRowProps {
    download: RevampDownload;
    articleID: number;
    uploader: string;
    professionalTitle?: string;
}

/**
 * One article file in the shared detail-page download row. Article downloads
 * are ungated — unlike the product page there is no tier to classify — so the
 * file opens directly and the click is logged once.
 */
const ArticleFileRow = ({
    download,
    articleID,
    uploader,
    professionalTitle,
}: ArticleFileRowProps) => {
    const tracking = useTracker();
    const hasBeenTracked = useRef(false);
    const [hasDownloaded, setHasDownloaded] = useState(false);

    const handleDownload = useCallback(() => {
        if (!download.link) return;

        if (!hasBeenTracked.current) {
            hasBeenTracked.current = true;
            tracking.log('DownloadsClick', {
                url: new URL(window.location.href),
                label: professionalTitle ?? '',
                data: {
                    ExtraData: download.title,
                    articleID,
                    fileTitle: download.title,
                    fileID: download.id,
                },
            });
        }

        window.open(download.link, '_blank', 'noopener');
        setHasDownloaded(true);
    }, [download, articleID, professionalTitle, tracking]);

    const Icon = download.isExternal
        ? ExternalLink
        : download.extension
          ? FileText
          : File;
    const format = download.isExternal
        ? 'Link'
        : (download.extension?.toUpperCase() ?? 'File');
    // External links have no server-side size, so they fall back to a dash.
    const size = download.sizeBytes ? formatFileSize(download.sizeBytes) : '—';

    return (
        <DownloadFileRow
            title={download.title}
            uploader={uploader}
            fileLabel={FILE_LABEL}
            format={format}
            size={size}
            Icon={Icon}
            hasDownloaded={hasDownloaded}
            onDownload={handleDownload}
        />
    );
};

/**
 * Content of the article's download accordion row: every file attached to the
 * article, in the same row the product page uses.
 */
export const RevampDownloadRow = ({
    downloads,
    articleID,
    professionalTitle,
}: RevampDownloadRowProps) => {
    if (downloads.length === 0) return null;

    const uploader = professionalTitle ?? ARCHIPRO;

    return (
        <div className="pb-[1.8rem] max-md:pb-4 md:space-y-[0.1875rem]">
            {downloads.map((download) => (
                <ArticleFileRow
                    key={download.id}
                    download={download}
                    articleID={articleID}
                    uploader={uploader}
                    professionalTitle={professionalTitle}
                />
            ))}
        </div>
    );
};
